import type { ApplyFunnelChannelSessionCacheUpdateParams } from '../../../types/cache/funnel-channel-sessions/update.types';

import type { InfiniteData } from '@tanstack/react-query';
import type {
  FunnelChannelSession,
  FunnelChannelSessionListResponse,
} from 'src/api/generated';
import {
  getChannelSessionQueryParams,
  parseFilterByExpression,
  shouldAddChannelSessionToQuery,
} from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import { updateCache } from 'src/modules/shared/utils/cache/update.util';

/**
 * Applies a realtime channel-session event to matching channel-session caches.
 *
 * @param params - Cache update inputs including the query client and action.
 */
export const applyFunnelChannelSessionCacheUpdate = ({
  queryClient,
  action,
  resourceId,
  funnelConversationId,
  channelSession,
}: ApplyFunnelChannelSessionCacheUpdateParams): void => {
  if (!channelSession && action !== 'deleted') {
    return;
  }

  const affectedQueries = cancelFunnelChannelSessionReads({
    queryClient,
    action,
    resourceId,
    funnelConversationId,
    channelSession,
  });

  const updatedData =
    channelSession ??
    ({
      id: Number(resourceId),
    } as FunnelChannelSession);

  updateCache({
    queryClient,
    queryKey: ['crm/funnel-channel-sessions'],
    updatedData,
    shouldAddItem:
      action === 'deleted' ? () => false : shouldAddChannelSessionToQuery,
    shouldRemoveItem: (_matchedKey, oldItem) =>
      oldItem.id.toString() === resourceId &&
      (action === 'deleted' ||
        (channelSession !== undefined &&
          !shouldAddChannelSessionToQuery(_matchedKey, channelSession))),
  });
  affectedQueries.forEach((query) => {
    if (query.state.data === undefined) {
      // A single event is not a complete list. Restart the existing active
      // query instead of leaving the mounted composer with no session data.
      void queryClient.refetchQueries({
        queryKey: query.queryKey,
        exact: true,
        type: 'active',
      });
    }
  });
};

/**
 * Cancels only pre-event session reads that may contain the affected resource.
 * @param params - Validated event identity and optional session snapshot.
 * @returns Queries whose ordinary HTTP reads may need recovery.
 */
export const cancelFunnelChannelSessionReads = ({
  queryClient,
  resourceId,
  funnelConversationId,
  channelSession,
}: ApplyFunnelChannelSessionCacheUpdateParams) => {
  const affectedQueries = queryClient
    .getQueryCache()
    .findAll({
      queryKey: ['crm/funnel-channel-sessions'],
    })
    .filter(({ queryKey }) => {
      const data =
        queryClient.getQueryData<
          InfiniteData<FunnelChannelSessionListResponse>
        >(queryKey);
      // A cached row must also be removed when its new snapshot leaves a filter.
      if (
        data?.pages.some((page) =>
          page.items.some((item) => String(item.id) === resourceId)
        )
      ) {
        return true;
      }
      const filters = parseFilterByExpression(
        getChannelSessionQueryParams(queryKey)?.filterBy
      );
      return (
        (!filters.funnelConversationId ||
          filters.funnelConversationId === String(funnelConversationId)) &&
        (!filters.id || filters.id === resourceId) &&
        (!filters.channelType ||
          !channelSession ||
          filters.channelType === String(channelSession.channelType))
      );
    });

  affectedQueries.forEach((query) => {
    // An earlier read must not restore an older reply window. Cancel only
    // affected lists, synchronously, without reverting accepted cache state.
    void queryClient.cancelQueries(
      { queryKey: query.queryKey, exact: true },
      { revert: false }
    );
  });
  return affectedQueries;
};
